import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Popover,
  Select,
  Switch,
  Tooltip,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import TuneIcon from "@mui/icons-material/Tune";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { RATE_OPTIONS, langPrefix, voicesForLang } from "../bible/speech";
import { loadSpeechPrefs, saveSpeechPrefs } from "../bible/speechPrefs";
import { useZenMode } from "../theme/ZenModeContext";

const FLOAT_POS_KEY = "bible-speech-float-pos";

const controlSx = {
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
};

const readFloatPos = () => {
  try {
    const raw = localStorage.getItem(FLOAT_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return parsed;
    }
  } catch (e) {}
  return null;
};

const formatTimerDisplay = (sleepTimer, labels) => {
  if (!sleepTimer) return null;
  if (sleepTimer.type === "chapter") {
    return "⏳ " + (labels.sleepTimerChapter || "读完停");
  }
  if (sleepTimer.type === "minutes" && typeof sleepTimer.remainingSeconds === "number") {
    const mins = Math.floor(sleepTimer.remainingSeconds / 60);
    const secs = sleepTimer.remainingSeconds % 60;
    return `⏳ ${mins}:${secs < 10 ? "0" : ""}${secs}`;
  }
  return null;
};

const SpeechBar = ({
  supported,
  status,
  current,
  index,
  total,
  hasSelection,
  labels,
  chapterTitle = "",
  chapterSubtitle = "",
  bookChapter = 0,
  bookTotalChapters = 0,
  onChapterClick,
  modes,
  mode,
  onModeChange,
  rate,
  onRateChange,
  autoAdvance,
  onAutoAdvanceChange,
  sleepTimer,
  onSleepTimerChange,
  voices = [],
  voiceURI = {},
  onVoiceChange,
  voiceLangs = [],
  onPlay,
  onPause,
  onResume,
  onStop,
  onSkip,
  sections = [],
  onPlaySection,
}) => {
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [sectionAnchor, setSectionAnchor] = useState(null);
  const [collapsed, setCollapsed] = useState(() => loadSpeechPrefs().collapsed);
  const [floatPos, setFloatPos] = useState(readFloatPos);
  const trigger = useScrollTrigger();
  const { isZenMode } = useZenMode();
  const isHeaderHidden = isZenMode || trigger;

  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
    hasMoved: false,
    pointerId: null,
  });

  const playing = status === "playing";
  const paused = status === "paused";
  const active = playing || paused;
  const playLabel = hasSelection ? labels.listenSelection : labels.listenChapter;
  const canSkipPrev = active && index > 0;
  const canSkipNext = active && index >= 0 && index < total - 1;
  const timerDisplay = formatTimerDisplay(sleepTimer, labels);
  const progressPercent =
    total > 0 && index >= 0
      ? Math.min(100, Math.round(((index + 1) / total) * 100))
      : 0;

  const bookPercent =
    bookChapter > 0 && bookTotalChapters > 0
      ? Math.min(
          100,
          ((bookChapter - 1 + progressPercent / 100) / bookTotalChapters) * 100
        )
      : 0;
  const bookLine = bookPercent > 0 && (
    <Box
      data-testid="book-progress"
      sx={{
        position: "absolute",
        left: 0,
        width: `${bookPercent}%`,
        bottom: 3,
        height: 1.5,
        bgcolor: "warning.main",
        opacity: 0.85,
        pointerEvents: "none",
        transition: "width 260ms ease",
      }}
    />
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      saveSpeechPrefs({ collapsed: next });
      return next;
    });
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    const target = event.target;
    // Don't start drag if clicking an interactive button directly
    if (target.closest("button") && !target.closest("[data-drag-handle]")) {
      return;
    }

    const elem = event.currentTarget;
    const rect = elem.getBoundingClientRect();
    const currentX = floatPos ? floatPos.x : rect.left;
    const currentY = floatPos ? floatPos.y : rect.top;

    dragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      startPosX: currentX,
      startPosY: currentY,
      hasMoved: false,
      pointerId: event.pointerId,
    };

    try {
      elem.setPointerCapture(event.pointerId);
    } catch (e) {}
  };

  const pillRef = useRef(null);

  const getPillWidth = () => {
    return pillRef.current ? pillRef.current.offsetWidth : 280;
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current.isDragging) return;

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;

    if (!dragRef.current.hasMoved && Math.hypot(deltaX, deltaY) > 4) {
      dragRef.current.hasMoved = true;
    }

    if (dragRef.current.hasMoved) {
      const nextX = dragRef.current.startPosX + deltaX;
      const nextY = dragRef.current.startPosY + deltaY;

      // Safe viewport boundary clamps using actual pill width
      const pillWidth = getPillWidth();
      const maxX = Math.max(8, window.innerWidth - pillWidth - 10);
      const clampedX = Math.max(8, Math.min(maxX, nextX));
      const clampedY = Math.max(50, Math.min(window.innerHeight - 60, nextY));

      const nextPos = { x: clampedX, y: clampedY };
      setFloatPos(nextPos);
    }
  };

  const handlePointerUp = (event) => {
    if (!dragRef.current.isDragging) return;
    dragRef.current.isDragging = false;

    try {
      if (dragRef.current.pointerId !== null) {
        event.currentTarget.releasePointerCapture(dragRef.current.pointerId);
      }
    } catch (e) {}

    if (dragRef.current.hasMoved && floatPos) {
      // Auto-snap to left or right edge without overflowing screen
      const pillWidth = getPillWidth();
      const maxX = Math.max(8, window.innerWidth - pillWidth - 12);
      let snappedPos = { ...floatPos };
      const midScreen = window.innerWidth / 2;
      if (floatPos.x + pillWidth / 2 < midScreen) {
        snappedPos.x = 12;
      } else {
        snappedPos.x = maxX;
      }
      setFloatPos(snappedPos);
      try {
        localStorage.setItem(FLOAT_POS_KEY, JSON.stringify(snappedPos));
      } catch (e) {}
    }
  };

  // Keep floating position in bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      setFloatPos((prev) => {
        if (!prev) return null;
        const pillWidth = getPillWidth();
        const maxX = Math.max(8, window.innerWidth - pillWidth - 12);
        const clampedX = Math.max(8, Math.min(maxX, prev.x));
        const clampedY = Math.max(50, Math.min(window.innerHeight - 60, prev.y));
        if (clampedX === prev.x && clampedY === prev.y) return prev;
        return { x: clampedX, y: clampedY };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const voiceGroups = useMemo(
    () =>
      (voiceLangs || []).map((lang) => ({
        lang,
        prefix: langPrefix(lang),
        options: voicesForLang(voices, lang),
      })),
    [voiceLangs, voices]
  );

  if (!supported) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        {labels.speechUnsupported}
      </Typography>
    );
  }

  if (collapsed) {
    const floatingStyle = floatPos
      ? {
          position: "fixed",
          left: `${floatPos.x}px`,
          top: `${floatPos.y}px`,
        }
      : {
          position: "fixed",
          right: { xs: 12, sm: 24 },
          top: { xs: 110, sm: 140 },
        };

    return (
      <Box
        ref={pillRef}
        role="region"
        aria-label={labels.listen}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          ...floatingStyle,
          maxWidth: "calc(100vw - 20px)",
          boxSizing: "border-box",
          zIndex: 1250,
          touchAction: "none",
          userSelect: "none",
          cursor: "grab",
          "&:active": {
            cursor: "grabbing",
          },
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          px: 1,
          py: 0.6,
          borderRadius: 999,
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(18, 26, 26, 0.92)"
              : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(16px)",
          border: "1px solid",
          borderColor: (theme) =>
            active
              ? "rgba(42, 111, 111, 0.45)"
              : theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(0, 0, 0, 0.12)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(42, 111, 111, 0.25)"
              : "0 8px 28px rgba(0, 0, 0, 0.16), 0 2px 6px rgba(42, 111, 111, 0.12)",
        }}
      >
        <Tooltip title="按住可拖动位置" disableInteractive>
          <Box
            data-drag-handle="true"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              opacity: 0.75,
              cursor: "grab",
              "&:hover": { opacity: 1 },
              pr: 0.2,
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>

        {active ? (
          <>
            {playing ? (
              <Tooltip title={labels.pause} disableInteractive>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={onPause}
                  aria-label={labels.pause}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.dark" },
                    p: 0.6,
                  }}
                >
                  <PauseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={labels.resume} disableInteractive>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={onResume}
                  aria-label={labels.resume}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.dark" },
                    p: 0.6,
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={labels.stop} disableInteractive>
              <IconButton
                size="small"
                onClick={onStop}
                aria-label={labels.stop}
                sx={controlSx}
              >
                <StopIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={labels.skipNext} disableInteractive>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onSkip(1)}
                  disabled={!canSkipNext}
                  aria-label={labels.skipNext}
                  sx={controlSx}
                >
                  <SkipNextIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                chapterTitle
                  ? `${chapterTitle}${current?.verse ? ` · ${labels.listeningVerse(current.verse, current.lang)}` : ""}${onChapterClick ? ` (${labels.scrollToTop || "回到顶部"})` : ""}`
                  : undefined
              }
              disableInteractive
            >
              <Typography
                variant="caption"
                color="text.primary"
                aria-live="polite"
                noWrap
                onClick={onChapterClick}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  maxWidth: { xs: 90, sm: 130 },
                  px: 0.3,
                  cursor: onChapterClick ? "pointer" : "default",
                }}
              >
                {current?.verse
                  ? `${chapterTitle ? `${chapterTitle} ` : ""}${labels.listeningVerse(current.verse, current.lang)}`
                  : (chapterTitle || labels.listen)}
              </Typography>
            </Tooltip>

            {timerDisplay && (
              <Typography
                variant="caption"
                sx={{
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 999,
                  fontSize: "0.7rem",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {timerDisplay}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Tooltip
              title={chapterTitle ? `${chapterTitle} · ${playLabel}` : playLabel}
              disableInteractive
            >
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
                onClick={onPlay}
                data-testid="speech-play"
                aria-pressed={false}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 1.2,
                  py: 0.3,
                  fontSize: "0.8rem",
                  minWidth: 0,
                }}
              >
                {playLabel}
              </Button>
            </Tooltip>
          </>
        )}

        <Tooltip title={labels.expandSpeech || "展开朗读"} disableInteractive>
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={labels.expandSpeech || "展开朗读"}
            sx={controlSx}
          >
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>

        {active && total > 0 && bookLine}
        {active && total > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 2,
              left: 14,
              right: 14,
              height: 2.5,
              borderRadius: 999,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.14)"
                  : "rgba(0, 0, 0, 0.08)",
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progressPercent}%`,
                bgcolor: "primary.main",
                borderRadius: 999,
                transition: "width 260ms ease",
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      role="region"
      aria-label={labels.listen}
      sx={{
        position: "sticky",
        top: isHeaderHidden ? 0 : { xs: 52, sm: 60 },
        zIndex: 8,
        mb: 2,
        px: { xs: 0.75, sm: 1.25 },
        py: 1,
        border: "1px solid",
        borderColor: active ? "transparent" : "divider",
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(18, 24, 24, 0.92)"
            : "rgba(255, 255, 255, 0.94)",
        backdropFilter: "blur(12px)",
        boxShadow: active ? "0 8px 24px rgba(42, 111, 111, 0.12)" : "none",
        outline: active ? "1px solid rgba(42, 111, 111, 0.28)" : "none",
        transition: (theme) =>
          theme.transitions.create(["top"], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeInOut,
          }),
      }}
    >
      {modes?.length > 1 && (
        <Box sx={{ display: "flex", gap: 0.75, mb: 1, flexWrap: "wrap" }}>
          {modes.map((item) => (
            <Button
              key={item.id}
              size="small"
              variant={mode === item.id ? "contained" : "outlined"}
              onClick={() => onModeChange(item.id)}
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 1.2,
                borderRadius: 999,
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.35, sm: 0.6 }, minWidth: 0 }}>
        {!active && (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon sx={{ fontSize: { xs: 15, sm: 17 } }} />}
            onClick={onPlay}
            data-testid="speech-play"
            aria-pressed={false}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: { xs: 1, sm: 1.6 },
              py: { xs: 0.35, sm: 0.5 },
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {playLabel}
          </Button>
        )}
        {sections.length > 0 && onPlaySection && (
          <Tooltip title={labels.listenSections} disableInteractive>
            <IconButton
              size="small"
              onClick={(event) => setSectionAnchor(event.currentTarget)}
              aria-label={labels.listenSections}
              aria-haspopup="menu"
              sx={{
                ...controlSx,
                p: { xs: 0.4, sm: 0.6 },
                flexShrink: 0,
              }}
            >
              <FormatListBulletedIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
            </IconButton>
          </Tooltip>
        )}
        {playing && (
          <Tooltip title={labels.pause} disableInteractive>
            <IconButton
              color="primary"
              onClick={onPause}
              aria-label={labels.pause}
              sx={{
                ...controlSx,
                p: { xs: 0.4, sm: 0.6 },
                flexShrink: 0,
              }}
            >
              <PauseIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
            </IconButton>
          </Tooltip>
        )}
        {paused && (
          <Tooltip title={labels.resume} disableInteractive>
            <IconButton
              color="primary"
              onClick={onResume}
              aria-label={labels.resume}
              sx={{
                ...controlSx,
                p: { xs: 0.4, sm: 0.6 },
                flexShrink: 0,
              }}
            >
              <PlayArrowIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
            </IconButton>
          </Tooltip>
        )}
        {active && (
          <>
            <Tooltip title={labels.stop} disableInteractive>
              <IconButton
                onClick={onStop}
                aria-label={labels.stop}
                sx={{
                  ...controlSx,
                  p: { xs: 0.4, sm: 0.6 },
                  flexShrink: 0,
                }}
              >
                <StopIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={labels.skipPrev} disableInteractive>
              <span>
                <IconButton
                  onClick={() => onSkip(-1)}
                  disabled={!canSkipPrev}
                  aria-label={labels.skipPrev}
                  sx={{
                    ...controlSx,
                    p: { xs: 0.4, sm: 0.6 },
                    flexShrink: 0,
                  }}
                >
                  <SkipPreviousIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={labels.skipNext} disableInteractive>
              <span>
                <IconButton
                  onClick={() => onSkip(1)}
                  disabled={!canSkipNext}
                  aria-label={labels.skipNext}
                  sx={{
                    ...controlSx,
                    p: { xs: 0.4, sm: 0.6 },
                    flexShrink: 0,
                  }}
                >
                  <SkipNextIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.4, sm: 0.75 },
            minWidth: 0,
            flex: 1,
            ml: { xs: 0.35, sm: 0.8 },
            mr: { xs: 0.25, sm: 0.6 },
            overflow: "hidden",
          }}
        >
          {chapterTitle && (
            <Tooltip
              title={
                onChapterClick
                  ? `${chapterTitle}${chapterSubtitle ? ` · ${chapterSubtitle}` : ""} · ${labels.scrollToTop || "点击回到章节顶部"}`
                  : `${chapterTitle}${chapterSubtitle ? ` · ${chapterSubtitle}` : ""}`
              }
              disableInteractive
            >
              <Box
                onClick={onChapterClick}
                role={onChapterClick ? "button" : undefined}
                tabIndex={onChapterClick ? 0 : undefined}
                onKeyDown={
                  onChapterClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onChapterClick();
                        }
                      }
                    : undefined
                }
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: 0.35, sm: 0.5 },
                  px: { xs: 0.5, sm: 0.85 },
                  py: { xs: 0.2, sm: 0.3 },
                  borderRadius: 1.5,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(42, 111, 111, 0.22)"
                      : "rgba(42, 111, 111, 0.08)",
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(42, 111, 111, 0.4)"
                      : "rgba(42, 111, 111, 0.22)",
                  color: "text.primary",
                  flexShrink: { xs: 1, sm: 0 },
                  minWidth: 0,
                  maxWidth: {
                    xs: active ? "105px" : "150px",
                    sm: "240px",
                    md: "none",
                  },
                  cursor: onChapterClick ? "pointer" : "default",
                  transition: "all 160ms ease",
                  "&:hover": onChapterClick
                    ? {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(42, 111, 111, 0.35)"
                            : "rgba(42, 111, 111, 0.16)",
                        borderColor: "primary.main",
                        color: "primary.main",
                      }
                    : undefined,
                }}
              >
                <MenuBookIcon
                  sx={{
                    fontSize: { xs: 13, sm: 15 },
                    color: "primary.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.75rem", sm: "0.82rem" },
                    lineHeight: 1.2,
                    letterSpacing: "0.01em",
                  }}
                >
                  {chapterTitle}
                </Typography>
              </Box>
            </Tooltip>
          )}

          {active ? (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-live="polite"
              noWrap
              sx={{
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                minWidth: 0,
                flexShrink: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {current?.verse ? (
                <>
                  <Box
                    component="span"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    {labels.listeningVerse(current.verse, current.lang)}
                  </Box>
                  {total > 1 && (
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        ml: 0.5,
                        opacity: 0.85,
                      }}
                    >
                      {` · ${index + 1}/${total} (${progressPercent}%)`}
                    </Box>
                  )}
                </>
              ) : (
                labels.listen
              )}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-live="polite"
              noWrap
              sx={{
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                minWidth: 0,
                flexShrink: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: { xs: chapterTitle ? "none" : "block", sm: "block" },
              }}
            >
              {chapterSubtitle ? (
                <>
                  <Box component="span" sx={{ fontStyle: "italic", opacity: 0.9 }}>
                    {chapterSubtitle}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: { xs: "none", md: "inline" },
                      ml: 0.75,
                      opacity: 0.7,
                    }}
                  >
                    {` · ${labels.speechHint}`}
                  </Box>
                </>
              ) : (
                labels.speechHint
              )}
            </Typography>
          )}
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={() =>
            onRateChange(
              RATE_OPTIONS[(RATE_OPTIONS.indexOf(rate) + 1) % RATE_OPTIONS.length]
            )
          }
          aria-label={labels.rate}
          sx={{
            minWidth: 0,
            px: { xs: 0.5, sm: 1 },
            py: { xs: 0.25, sm: 0.5 },
            borderRadius: 999,
            textTransform: "none",
            color: "text.secondary",
            fontSize: { xs: "0.72rem", sm: "0.8125rem" },
            flexShrink: 0,
          }}
        >
          {Number(rate).toFixed(1)}x
        </Button>
        {timerDisplay && (
          <Tooltip title={labels.sleepTimer || "定时停止"} disableInteractive>
            <Typography
              variant="caption"
              onClick={(event) =>
                setSettingsAnchor((current) => (current ? null : event.currentTarget))
              }
              sx={{
                cursor: "pointer",
                px: { xs: 0.6, sm: 1 },
                py: { xs: 0.2, sm: 0.35 },
                borderRadius: 999,
                fontSize: { xs: "0.68rem", sm: "0.75rem" },
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {timerDisplay}
            </Typography>
          </Tooltip>
        )}
        <Tooltip title={labels.moreControls} disableInteractive>
          <IconButton
            size="small"
            onClick={(event) =>
              setSettingsAnchor((current) => (current ? null : event.currentTarget))
            }
            aria-expanded={Boolean(settingsAnchor)}
            aria-label={labels.moreControls}
            color={settingsAnchor ? "primary" : "default"}
            sx={{ p: { xs: 0.4, sm: 0.6 }, flexShrink: 0 }}
          >
            <TuneIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={labels.collapseSpeech || "收起到侧边悬浮"} disableInteractive>
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={labels.collapseSpeech || "收起到侧边悬浮"}
            sx={{
              ...controlSx,
              p: { xs: 0.4, sm: 0.6 },
              flexShrink: 0,
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: { xs: 16, sm: 19 } }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={sectionAnchor}
        open={Boolean(sectionAnchor)}
        onClose={() => setSectionAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {sections.map((section) => (
          <MenuItem
            key={`${section.verse}-${section.title}`}
            onClick={() => {
              setSectionAnchor(null);
              onPlaySection(section.verse);
            }}
            sx={{ maxWidth: 320, whiteSpace: "normal" }}
          >
            <Typography variant="body2" component="span">
              {section.title}
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
                sx={{ ml: 1 }}
              >
                {section.verse}
              </Typography>
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { mt: 1, p: 1.5, width: 280, display: "grid", gap: 1.25 },
          },
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {labels.rate}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {RATE_OPTIONS.map((value) => (
              <Button
                key={value}
                size="small"
                variant={rate === value ? "contained" : "outlined"}
                onClick={() => onRateChange(value)}
                sx={{ textTransform: "none", minWidth: 0, px: 1.1, borderRadius: 999 }}
              >
                {value.toFixed(1)}x
              </Button>
            ))}
          </Box>
        </Box>

        {voiceGroups
          .filter((group) => group.options.length)
          .map((group) => (
            <FormControl key={group.prefix} size="small" fullWidth>
              <InputLabel>{labels.voice}</InputLabel>
              <Select
                label={labels.voice}
                value={voiceURI[group.prefix] || ""}
                onChange={(event) => onVoiceChange(group.prefix, event.target.value)}
              >
                <MenuItem value="">{labels.voiceAuto}</MenuItem>
                {group.options.map((voice) => (
                  <MenuItem key={voice.voiceURI} value={voice.voiceURI}>
                    {voice.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2">
            {autoAdvance ? labels.autoAdvanceOn : labels.autoAdvanceOff}
          </Typography>
          <Switch
            checked={Boolean(autoAdvance)}
            onChange={(event) => onAutoAdvanceChange(event.target.checked)}
            inputProps={{ "aria-label": labels.autoAdvance }}
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {labels.sleepTimer || "定时停止"}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {[
              { id: null, label: labels.sleepTimerOff || "关闭" },
              { id: 15, label: labels.sleepTimerMin ? labels.sleepTimerMin(15) : "15分钟" },
              { id: 30, label: labels.sleepTimerMin ? labels.sleepTimerMin(30) : "30分钟" },
              { id: 45, label: labels.sleepTimerMin ? labels.sleepTimerMin(45) : "45分钟" },
              { id: 60, label: labels.sleepTimerMin ? labels.sleepTimerMin(60) : "60分钟" },
              { id: "chapter", label: labels.sleepTimerChapter || "读完本章" },
            ].map((opt) => {
              const isActive =
                opt.id === null
                  ? !sleepTimer
                  : sleepTimer?.type === "chapter"
                  ? opt.id === "chapter"
                  : sleepTimer?.minutes === opt.id;
              return (
                <Button
                  key={String(opt.id)}
                  size="small"
                  variant={isActive ? "contained" : "outlined"}
                  onClick={() => onSleepTimerChange?.(opt.id)}
                  sx={{ textTransform: "none", minWidth: 0, px: 1, borderRadius: 999, fontSize: "0.75rem" }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Popover>

      {active && total > 0 && bookLine}
      {active && total > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.06)",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progressPercent}%`,
              bgcolor: "primary.main",
              transition: "width 260ms ease",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SpeechBar;
